import { useState, useCallback, useRef } from "react";
import { SimCanvas } from "@/components/SimCanvas";
import { createBall, DEFAULT_CONFIG } from "@/lib/physics";
import type { Ball, PhysicsConfig } from "@/lib/physics";
import { Button } from "@/components/ui/button";

export function Lesson5Energy() {
	const [balls, setBalls] = useState<Ball[]>(() => [
		createBall(350, 50, 200, 0, 25),
	]);
	const [config] = useState<PhysicsConfig>({
		...DEFAULT_CONFIG,
		restitution: 0.95,
		gravity: 800,
	});
	const [energyData, setEnergyData] = useState({ ke: 0, pe: 0, total: 0 });
	const barCanvasRef = useRef<HTMLCanvasElement>(null);

	const onFrame = useCallback((frameBalls: Ball[]) => {
		let ke = 0;
		let pe = 0;
		const height = 400;
		for (const b of frameBalls) {
			const speed = Math.sqrt(b.vel.x * b.vel.x + b.vel.y * b.vel.y);
			ke += 0.5 * b.mass * speed * speed / 1000000;
			pe += b.mass * config.gravity * (height - b.pos.y) / 1000000;
		}
		setEnergyData({ ke, pe, total: ke + pe });

		// Draw energy bars
		const canvas = barCanvasRef.current;
		if (!canvas) return;
		const ctx = canvas.getContext("2d");
		if (!ctx) return;

		const w = canvas.width;
		const h = canvas.height;
		ctx.fillStyle = "#0a0a0f";
		ctx.fillRect(0, 0, w, h);

		const maxE = Math.max(ke + pe, 1);
		const barW = 50;
		const gap = 30;
		const startX = (w - (barW * 3 + gap * 2)) / 2;

		// KE bar
		const keH = (ke / maxE) * (h - 40);
		const keGrad = ctx.createLinearGradient(0, h - keH - 20, 0, h - 20);
		keGrad.addColorStop(0, "#00f5d4");
		keGrad.addColorStop(1, "#00f5d466");
		ctx.fillStyle = keGrad;
		ctx.shadowColor = "#00f5d4";
		ctx.shadowBlur = 10;
		ctx.fillRect(startX, h - keH - 20, barW, keH);
		ctx.shadowBlur = 0;
		ctx.fillStyle = "#00f5d4";
		ctx.font = "11px monospace";
		ctx.textAlign = "center";
		ctx.fillText("KE", startX + barW / 2, h - 4);

		// PE bar
		const peH = (pe / maxE) * (h - 40);
		const peGrad = ctx.createLinearGradient(0, h - peH - 20, 0, h - 20);
		peGrad.addColorStop(0, "#ff006e");
		peGrad.addColorStop(1, "#ff006e66");
		ctx.fillStyle = peGrad;
		ctx.shadowColor = "#ff006e";
		ctx.shadowBlur = 10;
		ctx.fillRect(startX + barW + gap, h - peH - 20, barW, peH);
		ctx.shadowBlur = 0;
		ctx.fillStyle = "#ff006e";
		ctx.fillText("PE", startX + barW + gap + barW / 2, h - 4);

		// Total bar
		const totalH = ((ke + pe) / maxE) * (h - 40);
		const totalGrad = ctx.createLinearGradient(0, h - totalH - 20, 0, h - 20);
		totalGrad.addColorStop(0, "#fee440");
		totalGrad.addColorStop(1, "#fee44066");
		ctx.fillStyle = totalGrad;
		ctx.shadowColor = "#fee440";
		ctx.shadowBlur = 10;
		ctx.fillRect(startX + (barW + gap) * 2, h - totalH - 20, barW, totalH);
		ctx.shadowBlur = 0;
		ctx.fillStyle = "#fee440";
		ctx.fillText("Total", startX + (barW + gap) * 2 + barW / 2, h - 4);
	}, [config.gravity]);

	const reset = () => {
		setBalls([createBall(350, 50, 200, 0, 25)]);
	};

	const handleClick = useCallback((x: number, y: number) => {
		setBalls((prev) => [
			...prev,
			createBall(x, y, (Math.random() - 0.5) * 400, -200, 15 + Math.random() * 15),
		]);
	}, []);

	return (
		<div className="space-y-4">
			<div className="flex gap-2 items-center">
				<Button variant="outline" size="sm" onClick={reset}>
					Reset
				</Button>
				<div className="flex gap-4 text-xs font-mono ml-4">
					<span className="text-[#00f5d4]">KE: {energyData.ke.toFixed(1)}</span>
					<span className="text-[#ff006e]">PE: {energyData.pe.toFixed(1)}</span>
					<span className="text-[#fee440]">Total: {energyData.total.toFixed(1)}</span>
				</div>
			</div>

			<div className="flex gap-4 items-start">
				<div className="flex-1">
					<SimCanvas
						balls={balls}
						config={config}
						showTrails
						showEnergy
						onFrame={onFrame}
						onClick={handleClick}
						width={500}
						height={400}
					/>
				</div>
				<canvas
					ref={barCanvasRef}
					width={200}
					height={400}
					className="rounded-xl border border-white/10"
					style={{ maxHeight: 400 }}
				/>
			</div>

			<p className="text-sm text-muted-foreground">
				<strong>Energy Conservation:</strong> Watch kinetic energy (KE, <span className="text-[#00f5d4]">cyan</span>) convert to potential energy (PE, <span className="text-[#ff006e]">pink</span>) and back. At the top of a bounce, KE is minimum and PE is maximum. Total energy (<span className="text-[#fee440]">yellow</span>) slowly decreases due to the restitution coefficient (not perfectly elastic).
			</p>
		</div>
	);
}
